/**
 * GroupChatView - Main group chat interface.
 * Pink/light theme. Mobile-first with proper keyboard handling.
 */

import { useEffect, useState } from 'react'
import { useGroupChat } from '../GroupChatContext'
import { UsernameModal } from './UsernameModal'
import { MessageList } from './MessageList'
import { MessageInput } from './MessageInput'
import { TypingIndicator } from './TypingIndicator'
import { NotificationSettings } from './NotificationSettings'
import { Navigate, useNavigate } from 'react-router-dom'
import { BRAND_COLORS } from '../../../config/brandConfig'

export function GroupChatView() {
  const {
    isLoading,
    hasAccess,
    needsUsername,
    isConnected,
    error,
    currentRoom,
  } = useGroupChat()

  const navigate = useNavigate()
  const [showNotifications, setShowNotifications] = useState(false)

  // Fix mobile viewport height (accounts for browser chrome + keyboard)
  useEffect(() => {
    function setVH() {
      const vh = window.innerHeight * 0.01
      document.documentElement.style.setProperty('--vh', `${vh}px`)
    }
    setVH()
    window.addEventListener('resize', setVH)
    // Also handle iOS visual viewport for keyboard
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', setVH)
    }
    return () => {
      window.removeEventListener('resize', setVH)
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', setVH)
      }
    }
  }, [])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-pink-50 flex items-center justify-center">
        <div className="text-pink-400 text-lg animate-pulse">Loading chat...</div>
      </div>
    )
  }

  if (hasAccess === false) {
    return <Navigate to="/welcome" replace />
  }

  if (needsUsername) {
    return <UsernameModal />
  }

  if (error && !isConnected) {
    return (
      <div className="min-h-screen bg-pink-50 flex flex-col items-center justify-center p-6">
        <div className="text-6xl mb-6">🚫</div>
        <h1 className="text-gray-900 text-2xl font-bold mb-3">Unable to Access Chat</h1>
        <p className="text-gray-500 text-center max-w-md mb-6">{error}</p>
        <button
          onClick={() => navigate('/welcome')}
          className="px-6 py-3 rounded-lg text-white font-semibold"
          style={{ backgroundColor: BRAND_COLORS.primary }}
        >
          Back to Home
        </button>
      </div>
    )
  }

  return (
    <div
      className="bg-pink-50 flex flex-col overflow-hidden"
      style={{ height: 'calc(var(--vh, 1vh) * 100)' }}
    >
      {/* Header - fixed at top */}
      <div
        className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-pink-200 shadow-sm"
        style={{ backgroundColor: '#fff' }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate('/welcome')}
            className="flex-shrink-0 text-pink-400 hover:text-pink-600 transition-colors p-1"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M12.5 15L7.5 10L12.5 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <div className="min-w-0">
            <h1 className="text-gray-900 font-bold text-base truncate">
              {currentRoom?.name || 'Group Chat'}
            </h1>
            {!isConnected && (
              <p className="text-yellow-500 text-xs">Connecting...</p>
            )}
          </div>
        </div>

        {/* Notification bell */}
        <button
          onClick={() => setShowNotifications(true)}
          className="flex-shrink-0 p-2 text-pink-400 hover:text-pink-600 hover:bg-pink-50 rounded-full transition-colors"
          title="Notification settings"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </button>
      </div>

      {/* Notification settings modal */}
      <NotificationSettings isOpen={showNotifications} onClose={() => setShowNotifications(false)} />

      {/* Rate limit warning */}
      {error && isConnected && (
        <div className="flex-shrink-0 bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-yellow-700 text-sm text-center">
          {error}
        </div>
      )}

      {/* Messages - fills remaining space */}
      <MessageList />

      {/* Typing indicator */}
      <TypingIndicator />

      {/* Input - fixed at bottom */}
      <MessageInput />
    </div>
  )
}
